import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { LANG_PATH } from 'app-router/app-router';
import styles from 'components/navbar/index.module.scss';
import classname from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import LightBoxLogin from 'components/light-box-login';
import localDataService from 'service/local-data-service';
import { MEMBER_ITEM, memberItemName, PATHS } from 'config';
import mochi from 'images/loadingMochi.svg';
import repo from 'service/repository';
import amplitudeService from 'service/amplitude';
import UseScroll from 'utils/scroll-helper';

const cx = classname.bind(styles);

export const amplitudeNavbarEvent = (name, type) => {
  amplitudeService.track(name, { type: type, component: 'navbar' });
};

export const amplitudeFooterEvent = (name, type) => {
  amplitudeService.track(name, { type: type, component: 'footer' });
};

export class Menu {
  name;
  path;
  isInternalLink;

  /**
   * @param name (string)
   * @param path (string)
   * @param isInternalLink (boolean)
   */
  constructor(name, path, isInternalLink = true, id = '') {
    this.name = name;
    this.path = path;
    this.isInternalLink = isInternalLink;
    this.id = id;
  }
}

export class SubMenu {
  name;
  menus;

  /**
   * @param name (string)
   * @param menus (array)
   */
  constructor(name, menus) {
    this.name = name;
    this.menus = menus;
  }
}

export const amplitudeEvent = (menu, from) => {
  switch (menu.path) {
    case PATHS.REFUELING:
      amplitudeService.track('服務介紹頁-各業務介紹頁_點擊', {
        type: '加油',
        component: from,
      });
      break;
    case PATHS.PARKING:
      amplitudeService.track('服務介紹頁-各業務介紹頁_點擊', {
        type: '停車',
        component: from,
      });
      break;
    case PATHS.ON_STREET_PARKING:
      amplitudeService.track('服務介紹頁-各業務介紹頁_點擊', {
        type: '路停',
        component: from,
      });
      break;
    case PATHS.AUTO_DETAILING:
      amplitudeService.track('服務介紹頁-各業務介紹頁_點擊', {
        type: '愛車服務',
        component: from,
      });
      break;
    case PATHS.GAS_STATIONS:
      amplitudeService.track('服務介紹頁-支援場站_點擊', {
        type: '加油',
        component: from,
      });
      break;
    case PATHS.PARKINGLOTS:
      amplitudeService.track('服務介紹頁-支援場站_點擊', {
        type: '停車',
        component: from,
      });
      break;
    case PATHS.CAR_BEAUTY_STORES:
      amplitudeService.track('服務介紹頁-支援場站_點擊', {
        type: '美容廠',
        component: from,
      });
      break;
    case PATHS.AUTO_REPAIR_STORES:
      amplitudeService.track('服務介紹頁-支援場站_點擊', {
        type: '維修廠',
        component: from,
      });
      break;
    case PATHS.CAR_WASH_STORES:
      amplitudeService.track('服務介紹頁-支援場站_點擊', {
        type: '洗車場',
        component: from,
      });
      break;
    case PATHS.PROMOTION:
      amplitudeService.track('服務介紹頁-優惠活動_點擊', {
        component: from,
      });
      break;
    default:
      return '';
  }
};

const Menus = ({ menus, loginAvailable, onLogin, onLogout }) => {
  const { language } = useTranslation().i18n;

  return (
    <>
      <ul className={styles.menus}>
        {menus.map(menu => {
          if (menu instanceof Menu) {
            return <MenuItem key={menu.path} menu={menu} />;
          } else {
            return <SubMenuGroup key={menu.name} subMenu={menu} />;
          }
        })}
      </ul>
      {loginAvailable && language === 'tw' && <LoginMenu onLogin={onLogin} onLogout={onLogout} />}
    </>
  );
};

const MenuLink = ({ menu, classname }) => {
  const langMatch = useRouteMatch(LANG_PATH);
  const lang = langMatch.params.locale === undefined ? '' : `/${langMatch.params.locale}`;

  if (menu.isInternalLink) {
    return (
      <Link className={styles[classname]} to={lang + menu.path} onClick={() => amplitudeEvent(menu, 'navbar')}>
        {menu.name}
      </Link>
    );
  } else {
    return <a href={menu.path}>{menu.name}</a>;
  }
};

const MenuItem = ({ menu }) => {
  const pathname = ['/refueling', '/parking', '/on-street-parking', '/auto-detailing'];
  return (
    <li className={cx({ [menu.id]: true })}>
      <MenuLink
        menu={menu}
        classame={pathname.indexOf(menu.path) !== -1 ? pathname[pathname.indexOf(menu.path)].slice(1) : ''}
      />
    </li>
  );
};

const SubMenuItem = ({ menu }) => {
  return (
    <li>
      <MenuLink menu={menu} classname={menu.id} />
    </li>
  );
};

const SubMenuGroup = ({ subMenu }) => {
  return (
    <li className={styles.submenu}>
      <span className={styles.title}>{subMenu.name}</span>
      <ul>
        {subMenu.menus.map(menu => {
          return <SubMenuItem key={menu.path} menu={menu} />;
        })}
      </ul>
    </li>
  );
};

const LoginMenu = ({ onLogin, onLogout }) => {
  const [t] = useTranslation();

  if (localDataService.getAuth()) {
    return (
      <div className={styles['is-login']}>
        <div className={styles['login-title']}>
          <span className={styles['login-icon']}>
            <img src={mochi} alt="mochi" />
          </span>
          {t('navbar.member.my_mochipay')}
        </div>
        <ul>
          {Object.entries(MEMBER_ITEM).map(([key, value]) => {
            return (
              <li key={key}>
                <Link to={{ pathname: PATHS.USER, search: `tab=${value}` }}>{memberItemName(value)}</Link>
              </li>
            );
          })}
          <li className={styles.logout} onClick={onLogout}>
            <span>{t('navbar.member.logout')}</span>
          </li>
        </ul>
      </div>
    );
  } else {
    return (
      <div className={styles.login} onClick={onLogin}>
        登入
      </div>
    );
  }
};

const Content = ({ menus, mobileMenus, distributorIcon, loginAvailable = true, detectMenuShow }) => {
  const langMatch = useRouteMatch(LANG_PATH);
  const history = useHistory();
  const location = useLocation();
  const lang = langMatch.params.locale === undefined ? '' : `/${langMatch.params.locale}`;
  const { language } = useTranslation().i18n;
  const mobileMenuRef = useRef();
  const [show, setShow] = useState(false);
  const [loginShow, setLoginShow] = useState(false);
  const [t] = useTranslation();

  useEffect(() => {
    detectMenuShow(show);
  }, [show, detectMenuShow]);

  useEffect(() => {
    if (show) {
      disableBodyScroll(mobileMenuRef.current);
    } else {
      enableBodyScroll(mobileMenuRef.current);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [show]);

  useEffect(() => {
    setShow(false);
  }, [location]);

  const DistributorLogo = useCallback(() => {
    if (!distributorIcon) return <></>;
    return <img className={styles.distributor} src={distributorIcon} alt="distributor logo" />;
  }, [distributorIcon]);

  const Logo = () => {
    const logo = t('images:navbar_logo');
    const path = '/';
    return (
      <div className={styles.logo}>
        <Link to={lang + path}>
          <DistributorLogo />
          <img src={logo} alt="mochipay logo" />
        </Link>
      </div>
    );
  };

  const HamburgerMenu = () => {
    const menuClasses = cx({
      'hamburger-menu': true,
      show,
    });

    const handleMenuClick = () => {
      setShow(prev => !prev);
    };

    return (
      <div className={menuClasses} onClick={handleMenuClick}>
        <span>Menu</span>
      </div>
    );
  };

  const logout = async () => {
    const [err] = await repo.logout();
    if (err) {
      history.push({ pathname: PATHS.REDIRECT, search: `logout=expired` });
    } else {
      history.push({ pathname: PATHS.REDIRECT, search: `logout=success` });
    }
    localDataService.clearStorage();
    setShow(false);
  };

  const MobileLoginMenu = () => {
    if (localDataService.getAuth()) {
      return (
        <div className={styles['is-login']}>
          <div
            className={styles['login-title']}
            onClick={() => {
              history.push(PATHS.USER);
            }}
          >
            <span className={styles['login-icon']}>
              <img src={mochi} alt="mochi" />
            </span>
            {t('navbar.member.my_mochipay')}
          </div>
          <li className={styles.logout} onClick={logout}>
            <span>{t('navbar.member.logout')}</span>
          </li>
        </div>
      );
    } else {
      return (
        <div className={styles.login} onClick={() => setLoginShow(true)}>
          登入
        </div>
      );
    }
  };

  const MobileMenus = () => {
    return (
      <ul className={styles['mobile-menus']} ref={mobileMenuRef}>
        {mobileMenus.map((menus, key) => {
          // 場站區塊
          if (key === 1) {
            return (
              <div key={key} className={styles['location']}>
                <li className={styles['mobile-menu-title']}>{menus.title}</li>
                {menus.menus.map(menu => {
                  return <MenuItem key={menu.path} menu={menu} />;
                })}
              </div>
            );
          }

          // 一般
          return (
            <div key={key}>
              <li className={styles['mobile-menu-title']}>{menus.title}</li>
              {menus.menus.map(menu => {
                return <MenuItem key={menu.path} menu={menu} />;
              })}
            </div>
          );
        })}
        {loginAvailable && language === 'tw' && (
          <div>
            <li className={styles['mobile-menu-title']}>帳號</li>
            <MobileLoginMenu />
          </div>
        )}
      </ul>
    );
  };

  return (
    <>
      <LightBoxLogin
        show={loginShow}
        onDismiss={() => {
          setLoginShow(false);
        }}
      />
      {Logo()}
      <Menus menus={menus} loginAvailable={loginAvailable} onLogin={() => setLoginShow(true)} onLogout={logout} />
      <MobileMenus />
      {HamburgerMenu()}
    </>
  );
};

const Navbar = React.memo(props => {
  const [menuShow, setMenuShow] = useState(false);
  const location = useLocation();
  const isPoiPage = path => {
    const poiPath = [
      PATHS.GAS_STATIONS,
      PATHS.PARKINGLOTS,
      PATHS.AUTO_REPAIR_STORES,
      PATHS.CAR_BEAUTY_STORES,
      PATHS.CAR_WASH_STORES,
    ];
    return poiPath.some(x => path.includes(x));
  };

  const inSpecificPage = isPoiPage(location.pathname) || location.pathname.includes(PATHS.USER);
  const [move, scrollDown] = UseScroll();

  const navbarClasses = cx({
    navbar: true,
    move,
    hide: inSpecificPage ? scrollDown : false,
    show: menuShow,
    nudge: inSpecificPage,
  });

  const menus = useMemo(() => props.menus, [props.menus]);
  const mobileMenus = useMemo(() => props.mobileMenus, [props.mobileMenus]);
  const detectMenuShow = useCallback(show => setMenuShow(show), []);

  return (
    <div className={navbarClasses}>
      <Content menus={menus} mobileMenus={mobileMenus} detectMenuShow={detectMenuShow} {...props} />
    </div>
  );
});

export default Navbar;
